import { TypeChecker } from '@attain-apps/type-guards'
import { plugins } from 'pretty-format'

import type { ClipboardService } from '../clipboard.service'
import type * as models from '../models'

import { State } from './state'

export class Selectors {
  /**
   * All previously copied items, with their respective ids.
   *
   * @see {@link State.lookup}, {@link models.Item.Identified}
   */
  private readonly _items
  /**
   * Get items from a state, optionally filtering them by a predicate.
   *
   * @see {@link _items}
   */
  private readonly _getItems
  /**
   * @see {@link State.dialogConfig}
   */
  private readonly _dialogConfig
  /**
   * @see {@link models.DialogConfig.originPosition}
   */
  private readonly _dialogOriginPosition
  /**
   * @see {@link models.DialogConfig.offset}
   */
  private readonly _dialogOffset
  /**
   * @see {@link State.paster}
   */
  public readonly paster
  /**
   * Selected items in context of clipboard
   *
   * @see {@link State.selectedIds}
   */
  public readonly selectedIds
  /**
   * The dialog is in paste mode if
   * there is a {@link State.pasteClassRef} and we are pasting {@link models.Item}.
   */
  public readonly isPasteMode
  /**
   * TODO: be more vocal in the documentation
   * Can we paste items
   *
   * @see {@link State.pasteClassRef}
   */
  public readonly canPaste
  /**
   * Similar to {@link _items} but if {@link _pasteClassRef} is set, it filters
   * items by that class.
   */
  public readonly items
  /**
   * Same as {@link items}, but filtered by {@link selectedIds}.
   */
  public readonly selectedItems
  /**
   * Is item selected in context of dialog
   *
   * @see {@link State.selectedIds}
   */
  public readonly isSelected
  /**
   * Number of selected items
   */
  public readonly selectedCount
  /**
   * Are all items selected
   *
   * @see {@link State.selectedIds}
   */
  public readonly isAllSelected
  /**
   * Are some items selected
   *
   * @see {@link State.selectedIds}
   */
  public readonly isSomeSelected
  /**
   * Should we present clipboard dialog
   *
   * @see {@link State.dialogConfig}
   */
  public readonly isDialogOpen
  /**
   * Dialog `x` position
   *
   * @default 0
   * @see {@link _dialogOriginPosition}
   */
  public readonly dialogPositionX
  /**
   * Dialog `y` position
   *
   * @default 0
   * @see {@link _dialogOriginPosition}
   */
  public readonly dialogPositionY
  /**
   * dialog offset `x`
   *
   * @default 0
   * @see {@link _dialogOffset}
   */
  public readonly dialogOffsetX
  /**
   * dialog offset `y`
   *
   * @default 0
   * @see {@link _dialogOffset}
   */
  public readonly dialogOffsetY

  constructor(service: ClipboardService) {
    this._dialogConfig = service._feature.state.project(
      state => state.dialogConfig,
    )
    this._dialogOriginPosition = this._dialogConfig.project(
      config => config?.originPosition,
    )
    this._dialogOffset = this._dialogConfig.project(config => config?.offset)

    this.isDialogOpen = this._dialogConfig.project(
      dialogPosition => !!dialogPosition,
    )
    this.dialogOffsetX = this._dialogOffset.project(offset =>
      offset ? offset.x : 0,
    )
    this.dialogOffsetY = this._dialogOffset.project(offset =>
      offset ? offset.y : 0,
    )
    this.dialogPositionX = this._dialogOriginPosition.project(position =>
      position ? position.x : 0,
    )
    this.dialogPositionY = this._dialogOriginPosition.project(position =>
      position ? position.y : 0,
    )
    this._items = service._feature.state.project(state =>
      Object.entries(state.lookup)
        // Explicity typing return, so we prevent some errors in case
        // 'models.Item.Identified' is changed in the future
        .map(([id, item]): models.Item.Identified | undefined =>
          item ? { item, id } : undefined,
        )
        // The lookup is of type Partial<Record<Foo, Bra>>, so we need to filter
        // out "undefined" values, even though there shouldn't be any.
        .filter(TypeChecker.isNotNullish.raw),
    )
    this._getItems = (predicate: (item: models.Item.Identified) => boolean) =>
      this._items.project(items => items.filter(predicate))

    this.selectedIds = service._feature.state.project(
      state => state.selectedIds,
    )

    this.paster = service._feature.state.project(state => state.paster)

    this.canPaste = service._feature.createSelectorMethod(
      ({ pasteClassRef }: { readonly pasteClassRef: models.Item.Class }) =>
        this._getItems(
          identifiable => identifiable.item instanceof pasteClassRef,
        ).project(items => items.length > 0).asSelector,
    )

    this.isPasteMode = this.paster.project(paster => !!paster)

    this.items = this._items.project(this.paster.asSelector, (items, paster) =>
      paster ? items.filter(({ item }) => paster.canPaste(item)) : items,
    )

    this.selectedItems = this.items.project(
      this.selectedIds.asSelector,
      (items, selectedIds) => items.filter(({ id }) => selectedIds.has(id)),
    )

    this.isSelected = service._feature.createSelectorMethod(
      ({
        id,
        identifiable,
      }: ExactlyOneOf<{
        readonly identifiable: models.Item.Identified
        readonly id: string
      }>) => {
        if (identifiable) {
          id = identifiable.id
        }

        return this.selectedIds.project(selectedIdentifiables =>
          id ? selectedIdentifiables.has(id) : false,
        ).asSelector
      },
    )

    this.selectedCount = this.selectedIds.project(
      selectedIds => selectedIds.size,
    )
    this.isSomeSelected = this.selectedCount.project(count => count > 0)
    this.isAllSelected = this.selectedCount.project(
      this.items.asSelector,
      (count, items) => count === items.length,
    )
  }
}
